import React from 'react';
import PropTypes from 'prop-types';
import './riskScorePanel.scss';

const BAND_CONFIG = {
  LOW: {
    label: 'Low Risk',
    tone: 'low',
    summary: 'The financial profile shows few risk indicators.'
  },
  MODERATE: {
    label: 'Moderate Risk',
    tone: 'moderate',
    summary: 'Some risk indicators are present. Review the findings below.'
  },
  HIGH: {
    label: 'High Risk',
    tone: 'high',
    summary: 'Multiple significant risk indicators found. Careful review advised.'
  },
  UNKNOWN: {
    label: 'Not Assessed',
    tone: 'unknown',
    summary: 'Insufficient data to compute a risk score.'
  }
};

const RiskScorePanel = ({ assessment }) => {
  if (!assessment) {
    return (
      <section className="risk-panel risk-panel--empty">
        <div className="risk-panel__empty-body">
          <span className="risk-panel__empty-title">No risk assessment</span>
          <span className="risk-panel__empty-text">
            {`A financial risk score is generated from the applicant's bank statement. No statement
            has been analysed for this application yet.`}
          </span>
        </div>
      </section>
    );
  }

  const band = BAND_CONFIG[assessment.band] ?? BAND_CONFIG.UNKNOWN;
  const score = assessment.score;

  // Score ring geometry
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const offset = score != null ? circumference - (score / 100) * circumference : circumference;

  // Split findings into negatives (risks) and positives (mitigants).
  const risks = (assessment.findings ?? []).filter((f) => f.impact < 0);
  const positives = (assessment.findings ?? []).filter((f) => f.impact > 0);
  const neutral = (assessment.findings ?? []).filter((f) => f.impact === 0);

  return (
    <section className={`risk-panel risk-panel--${band.tone}`}>
      <header className="risk-panel__head">
        <span className="risk-panel__tag">Financial Risk Assessment</span>
        {assessment.evaluatedAt && (
          <span className="risk-panel__time">
            {new Date(assessment.evaluatedAt).toLocaleString('en-IN', {
              day: 'numeric',
              month: 'short',
              hour: 'numeric',
              minute: '2-digit'
            })}
          </span>
        )}
      </header>

      <div className="risk-panel__summary">
        <div className="risk-ring">
          <svg viewBox="0 0 100 100" className="risk-ring__svg" aria-hidden="true">
            <circle cx="50" cy="50" r={radius} className="risk-ring__track" />
            <circle
              cx="50"
              cy="50"
              r={radius}
              className="risk-ring__fill"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              transform="rotate(-90 50 50)"
            />
          </svg>
          <div className="risk-ring__center">
            <span className="risk-ring__value numeric">{score ?? '—'}</span>
            <span className="risk-ring__max numeric">/100</span>
          </div>
        </div>

        <div className="risk-panel__verdict">
          <span className={`risk-badge risk-badge--${band.tone}`}>{band.label}</span>
          <p className="risk-panel__verdict-text">{band.summary}</p>
        </div>
      </div>

      {risks.length > 0 && (
        <div className="risk-findings">
          <span className="risk-findings__heading">Risk factors</span>
          {risks.map((f, i) => (
            <div key={i} className="finding finding--risk">
              <span className="finding__impact numeric">{f.impact}</span>
              <div className="finding__body">
                <span className="finding__factor">
                  {f.factor}
                  {f.value && <span className="finding__value numeric"> · {f.value}</span>}
                </span>
                <span className="finding__detail">{f.detail}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {positives.length > 0 && (
        <div className="risk-findings">
          <span className="risk-findings__heading">Positive factors</span>
          {positives.map((f, i) => (
            <div key={i} className="finding finding--positive">
              <span className="finding__impact numeric">+{f.impact}</span>
              <div className="finding__body">
                <span className="finding__factor">
                  {f.factor}
                  {f.value && <span className="finding__value numeric"> · {f.value}</span>}
                </span>
                <span className="finding__detail">{f.detail}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {risks.length === 0 && positives.length === 0 && neutral.length > 0 && (
        <p className="risk-panel__clean">{neutral[0].detail}</p>
      )}

      {assessment.insights && (
        <div className="risk-findings">
          <span className="risk-findings__heading">Additional insights</span>
          <div className="risk-insights">
            <div className="risk-insight">
              <span className="risk-insight__label">Salary regularity</span>
              <span className="risk-insight__value">
                {assessment.insights.salaryRegular ? 'Regular' : 'Irregular'}
              </span>
            </div>
            <div className="risk-insight">
              <span className="risk-insight__label">Income sources</span>
              <span className="risk-insight__value">
                {assessment.insights.distinctIncomeSources ?? '—'}
              </span>
            </div>
            <div className="risk-insight">
              <span className="risk-insight__label">Overdrafts</span>
              <span className="risk-insight__value">
                {assessment.insights.overdraftCount ?? '—'}
              </span>
            </div>
            <div className="risk-insight">
              <span className="risk-insight__label">Gambling activity</span>
              <span
                className={`risk-insight__value ${assessment.insights.gamblingDetected ? 'risk-insight__value--flag' : ''}`}>
                {assessment.insights.gamblingDetected ? 'Detected' : 'None'}
              </span>
            </div>
            <div className="risk-insight">
              <span className="risk-insight__label">Subscriptions</span>
              <span className="risk-insight__value">{assessment.insights.subscriptionCount}</span>
            </div>
            <div className="risk-insight">
              <span className="risk-insight__label">Utility payments</span>
              <span className="risk-insight__value">
                {assessment.insights.utilityPaymentsOnTime ? 'On time' : 'Irregular'}
              </span>
            </div>
            <div className="risk-insight">
              <span className="risk-insight__label">Large deposits</span>
              <span className="risk-insight__value">{assessment.insights.largeDepositCount}</span>
            </div>
            <div className="risk-insight">
              <span className="risk-insight__label">Balance trend</span>
              <span className="risk-insight__value">
                {assessment.insights.monthEndBalanceTrend || '—'}
              </span>
            </div>
            <div className="risk-insight">
              <span className="risk-insight__label">Top spend</span>
              <span className="risk-insight__value">
                {assessment.insights.topSpendCategory || '—'}
              </span>
            </div>
            <div className="risk-insight">
              <span className="risk-insight__label">Avg monthly expense</span>
              <span className="risk-insight__value">
                {Number(assessment.insights.avgMonthlyExpense) > 0
                  ? `₹${Number(assessment.insights.avgMonthlyExpense).toLocaleString('en-IN')}`
                  : '—'}
              </span>
            </div>
          </div>
        </div>
      )}

      <footer className="risk-panel__foot">
        {`This score is computed from the applicant's bank statement, weighted by percentage of
        income. It is a decision aid — the underwriter makes the final call.`}
      </footer>
    </section>
  );
};

RiskScorePanel.propTypes = {
  assessment: PropTypes.shape({
    score: PropTypes.number,
    band: PropTypes.string.isRequired,
    evaluatedAt: PropTypes.string,
    findings: PropTypes.arrayOf(
      PropTypes.shape({
        factor: PropTypes.string.isRequired,
        impact: PropTypes.number.isRequired,
        value: PropTypes.string,
        detail: PropTypes.string.isRequired
      })
    ).isRequired,
    insights: PropTypes.shape({
      salaryRegular: PropTypes.bool,
      distinctIncomeSources: PropTypes.number,
      overdraftCount: PropTypes.number,
      gamblingDetected: PropTypes.bool,
      subscriptionCount: PropTypes.number,
      utilityPaymentsOnTime: PropTypes.bool,
      largeDepositCount: PropTypes.number,
      monthEndBalanceTrend: PropTypes.string,
      topSpendCategory: PropTypes.string,
      avgMonthlyExpense: PropTypes.number
    })
  })
};

export default RiskScorePanel;
